import React, { lazy } from 'react';
import { SectionWrapper } from '../ui/SectionWrapper';
import { FadeUp } from '../ui/FadeUp';
import { CTAButton } from '../ui/CTAButton';
export function SystemSection() {
  const phases = [{
    id: '1',
    label: 'Fase 01',
    title: 'Descarga',
    description: 'Liberá la carga acumulada y cortá la cadena de reacción antes de que siga escalando.',
    time: '⏱ En menos de 15 minutos'
  }, {
    id: '2',
    label: 'Fase 02',
    title: 'Armonización',
    description: 'Reconectá con tu centro interno y recuperá tu eje sin depender de que el entorno cambie.',
    time: '⏱ Proceso guiado'
  }, {
    id: '3',
    label: 'Fase 03',
    title: 'Limpieza de Ambientes',
    description: 'Ordená tu entorno para sostener tu estabilidad y que el espacio no te robe la energía que recuperaste.',
    time: '⏱ Rutina sostenible'
  }];
  return <SectionWrapper bgColor="ivory" id="system" data-id="element-180">
      <div className="mb-10" data-id="element-181">
        <FadeUp data-id="element-182">
          <p className="text-[10px] tracking-[0.2em] uppercase text-plum font-medium mb-3" data-id="element-183">
            Sistema ProMind Energy™
          </p>
          <h2 className="text-[clamp(32px,9vw,48px)] mb-4" data-id="element-184">
            No es una práctica.
            <br data-id="element-185" />
            Es un <em className="italic text-plum" data-id="element-186">sistema</em> completo.
          </h2>

          {/* Mockup image */}
          <div className="my-5 rounded-[8px] overflow-hidden bg-sand/50" data-id="element-187">
            <img src="https://cdn.magicpatterns.com/uploads/x1MxSdjTcdsunn3DoGh2FN/mockup.webp" alt="Mockup del Método ProMind Energy con todos los materiales incluidos" width={800} height={600} loading="lazy" className="w-full h-auto object-cover" style={{
            aspectRatio: '4/3'
          }} data-id="element-188" />
          </div>

          <p className="text-[14px] text-muted leading-[1.75]" data-id="element-189">
            En menos de 15 minutos, vas a saber exactamente qué hacer para
            volver a tu centro — y poder repetirlo cada vez que lo necesités.
          </p>
        </FadeUp>
      </div>

      {/* System vs Method */}
      <FadeUp delay={0.1} data-id="element-190">
        <div className="bg-plum-deep text-ivory rounded-[12px] p-7 my-4 relative overflow-hidden" data-id="element-191">
          <h3 className="text-[13px] tracking-[0.15em] uppercase text-gold-light mb-4 font-sans font-medium relative z-10" data-id="element-192">
            ¿Por qué un sistema y no un método?
          </h3>

          <div className="grid grid-cols-2 gap-3 relative z-10" data-id="element-193">
            <div data-id="element-194">
              <p className="text-[11px] tracking-[0.12em] uppercase mb-2.5 opacity-50" data-id="element-195">
                Un método...
              </p>
              <ul className="space-y-2" data-id="element-196">
                {['Te da una técnica', 'Depende del contexto', 'Funciona en el momento', 'Lo aplicás y listo'].map((item, i) => <li key={i} className="text-[12px] text-white/70 pl-2.5 border-l border-white/15 leading-[1.4]" data-id="element-197">
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-198">
              <p className="text-[11px] tracking-[0.12em] uppercase mb-2.5 text-gold-light" data-id="element-199">
                Un sistema...
              </p>
              <ul className="space-y-2" data-id="element-200">
                {['Te da una estructura completa', 'Funciona en cualquier situación', 'Genera resultados sostenibles', 'Lo integrás a tu vida'].map((item, i) => <li key={i} className="text-[12px] text-gold-light pl-2.5 border-l border-gold leading-[1.4]" data-id="element-201">
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </FadeUp>

      {/* Phases */}
      <FadeUp delay={0.2} data-id="element-202">
        <p className="text-[13px] tracking-[0.12em] uppercase text-muted mb-5 font-medium" data-id="element-203">
          El Sistema funciona en 3 fases
        </p>
      </FadeUp>

      <div className="space-y-3" data-id="element-204">
        {phases.map((phase, index) => <FadeUp key={phase.id} delay={0.2 + index * 0.1} data-id="element-205">
            <div className="bg-white rounded-[10px] p-5 grid grid-cols-[48px_1fr] gap-4 items-start shadow-[0_2px_16px_rgba(0,0,0,0.04)] active:scale-[0.99] transition-transform" data-id="element-206">
              <div className="w-12 h-12 bg-plum text-white rounded-full flex items-center justify-center font-serif text-[22px] font-light flex-shrink-0" data-id="element-207">
                {phase.id}
              </div>
              <div data-id="element-208">
                <p className="text-[9px] tracking-[0.2em] uppercase text-muted mb-1" data-id="element-209">
                  {phase.label}
                </p>
                <p className="font-serif text-[22px] font-medium text-charcoal mb-1.5 leading-none" data-id="element-210">
                  {phase.title}
                </p>
                <p className="text-[13px] text-muted leading-[1.6]" data-id="element-211">
                  {phase.description}
                </p>
                <span className="inline-block mt-2 text-[11px] text-plum font-medium tracking-[0.05em]" data-id="element-212">
                  {phase.time}
                </span>
              </div>
            </div>
          </FadeUp>)}
      </div>

      <FadeUp delay={0.5} data-id="element-213">
        <div className="bg-sand rounded-[8px] p-[18px] text-[13px] text-charcoal leading-[1.65] text-center mt-4" data-id="element-214">
          <strong className="font-serif text-[17px] block mb-1.5 font-medium" data-id="element-215">
            "ProMind Energy es un sistema de higiene energética"
          </strong>
          No nació de la teoría. Nació de observar un patrón repetido en mujeres
          altamente responsables.
        </div>
      </FadeUp>

      <FadeUp delay={0.6} data-id="element-216">
        <div className="mt-8" data-id="element-217">
          <CTAButton data-id="element-218">Quiero acceder al sistema</CTAButton>
        </div>
      </FadeUp>
    </SectionWrapper>;
}